/**
 * @fileoverview DataBound widget — read-only chip pulled from external data.
 * @module inspection-renderer/fields
 *
 * Responsibilities:
 * - Phase 4 wires the host-injected `ResolveContext.resolveBoundField()`
 *   so the widget pulls the canonical value from the host's Dexie service
 *   when `extras.dataSource`/`extras.boundField` are set.
 * - Falls back to `value` prop, then `extras.resolvedLabel`, then
 *   `extras.placeholder`, then '—'.
 * - Honors `extras.requiredModule` (G-002) by returning `null` when the
 *   host reports the module is absent.
 * - Always read-only (no onChange) — write-back via `defaultMode:'editable'`
 *   is deferred to Phase 5 adapter.
 *
 * @component DataBoundField
 *
 * @contract DataBoundFieldProps
 * Purpose: prop contract for the read-only data-bound surface. Host injects
 * the resolved value via `value` or via the `ResolveContext` provider.
 */
import { FieldWrapper } from './FieldWrapper';
import type { InspectionFormField, ResolvedFieldStateV2 } from '@firesquirrel/core-domain';
import { useResolveContext } from '../contracts/rendererContext';

export interface DataBoundFieldProps {
  field: InspectionFormField;
  value: string | number | null | undefined;
  resolved?: ResolvedFieldStateV2;
  /** Set by the host when the bulk-pilot lock is engaged (D5). */
  bulkPilotMode?: boolean;
  /** Inspector-edit write-back handler (only invoked when defaultMode==='editable'). */
  onChange?: (next: string | number | null) => void;
  /** True when the inspector has typed over the resolved value at least once. */
  wasModifiedByInspector?: boolean;
  requiredUnmet?: boolean;
}

export function DataBoundField({ field, value, resolved, bulkPilotMode, onChange, wasModifiedByInspector, requiredUnmet }: DataBoundFieldProps) {
  const extras = (field.extras ?? {}) as {
    resolvedLabel?: string;
    placeholder?: string;
    dataSource?: string;
    boundField?: string;
    compositeFields?: string[];
    serviceAreaTypeCode?: string;
    entityCategoryFilter?: string;
    requiredModule?: 'contacts' | 'violations' | 'header';
    defaultMode?: 'readOnly' | 'editable';
    forceReadOnly?: boolean;
    inputType?: 'text' | 'textarea' | 'number' | 'toggle' | 'dropdown' | 'date' | 'address' | 'phone' | 'email' | 'lookup' | 'currency';
    displayFormat?: string;
  };
  const ctx = useResolveContext();
  // Mode resolution:
  // 1. `forceReadOnly: true` → always read-only.
  // 2. `bulkPilotMode` (D5) → always read-only.
  // 3. `extras.defaultMode === 'editable'` AND host wired `onChange` → editable.
  // 4. Default → read-only.
  const editable = !extras.forceReadOnly
    && !bulkPilotMode
    && extras.defaultMode === 'editable'
    && typeof onChange === 'function';

  // G-002 — hide when required module absent.
  if (extras.requiredModule && ctx && !ctx.hasModule(extras.requiredModule)) {
    return null;
  }

  // Pull from the context when the explicit prop value is null/undefined.
  let resolvedFromCtx: unknown = null;
  if ((value == null) && ctx && extras.dataSource && extras.boundField) {
    resolvedFromCtx = ctx.resolveBoundField({
      dataSource: extras.dataSource,
      boundField: extras.boundField,
      composite: extras.compositeFields,
      serviceAreaTypeCode: extras.serviceAreaTypeCode,
      entityCategoryFilter: extras.entityCategoryFilter,
    });
  }

  const display = value ?? resolvedFromCtx ?? extras.resolvedLabel ?? extras.placeholder ?? '—';
  const hasResolvedValue = value != null || resolvedFromCtx != null;

  return (
    <FieldWrapper
      fieldId={field.id}
      widgetType="dataBound"
      label={field.label}
      helpText={field.helpText}
      required={field.required}
      resolved={resolved}
      isNA={false}
      naEnabled={false}
      requiredUnmet={requiredUnmet}
    >
      {editable ? (
        <input
          id={field.id}
          type={extras.inputType === 'number' ? 'number' : 'text'}
          value={value == null ? String(resolvedFromCtx ?? '') : String(value)}
          onChange={(e) => {
            const v = e.target.value;
            onChange?.(extras.inputType === 'number' ? (v === '' ? null : Number(v)) : v);
          }}
          data-resolved={hasResolvedValue ? 'true' : 'false'}
          data-data-source={extras.dataSource ?? ''}
          data-bound-field={extras.boundField ?? ''}
          data-default-mode={extras.defaultMode ?? 'readOnly'}
          data-was-modified={wasModifiedByInspector ? 'true' : 'false'}
          data-input-type={extras.inputType ?? ''}
          data-display-format={extras.displayFormat ?? ''}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-sm outline-hidden focus:ring-2 focus:ring-blue-300"
        />
      ) : (
        <div
          role="textbox"
          aria-readonly="true"
          aria-label={field.label}
          data-resolved={hasResolvedValue ? 'true' : 'false'}
          data-data-source={extras.dataSource ?? ''}
          data-bound-field={extras.boundField ?? ''}
          data-default-mode={extras.defaultMode ?? 'readOnly'}
          data-force-read-only={extras.forceReadOnly ? 'true' : 'false'}
          data-bulk-pilot-locked={bulkPilotMode ? 'true' : 'false'}
          data-was-modified={wasModifiedByInspector ? 'true' : 'false'}
          data-input-type={extras.inputType ?? ''}
          data-display-format={extras.displayFormat ?? ''}
          className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-sm text-sm text-gray-700"
        >
          {String(display)}
        </div>
      )}
    </FieldWrapper>
  );
}
